/* ============================================================
   THE TEDDY SOCIETY — Product page module
   File: assets/tts-product.js
   Provides:
   - <tts-product-form> : variant resolution, price/media/URL
     updates, AJAX add-to-bag into the cart drawer
   - <tts-gallery>      : thumbnail gallery
   - <tts-sticky-atc>   : sticky mobile add-to-bag bar
   - <tts-modal-drawer> : generic drawer (size guide)
   Requires tts-cart.js.
   ============================================================ */
(function () {
  'use strict';

  function moneyFormatOf(el) {
    return el.getAttribute('data-money-format') || '£{{amount}}';
  }

  /* ----------------------------------------------------------
     <tts-product-form>
     Markup contract:
     - <script type="application/json" data-tts-variants> product JSON
     - option inputs: input[name="option-{position}"]
     - [data-tts-price], [data-tts-compare-price]
     - [data-tts-atc] submit button, [data-tts-atc-text]
     - [data-tts-stock], [data-tts-error]
     - hidden input[name="id"]
     ---------------------------------------------------------- */
  var ProductForm = function () {
    var instance = Reflect.construct(HTMLElement, [], ProductForm);
    return instance;
  };
  ProductForm.prototype = Object.create(HTMLElement.prototype);
  ProductForm.prototype.constructor = ProductForm;
  Object.setPrototypeOf(ProductForm, HTMLElement);

  ProductForm.prototype.connectedCallback = function () {
    var dataEl = this.querySelector('[data-tts-variants]');
    if (!dataEl) return;
    try {
      this.product = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('input[name="id"]');
    this.moneyFormat = moneyFormatOf(this);
    this.currentVariant = this.findVariantById(parseInt(this.idInput.value, 10)) || null;

    // Designs (bear characters + "Just the Logo") for the "Design" option,
    // embedded by <tts-gallery> — a sibling, so query the document.
    var designsEl = document.querySelector('[data-tts-designs]');
    this.designs = [];
    if (designsEl) {
      try { this.designs = JSON.parse(designsEl.textContent) || []; } catch (e) { this.designs = []; }
    }
    this.designOptionIndex = (this.product.options || []).findIndex(function (name) {
      return (name || '').toLowerCase() === 'design';
    });

    this.applyUrlPreselection();

    var self = this;

    this.addEventListener('change', function (event) {
      if (event.target.matches('[data-tts-option-input]')) {
        self.onOptionChange();
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.addToBag();
      });
    }

    // Quantity buttons
    this.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-tts-qty-change]');
      if (!btn) return;
      event.preventDefault();
      var input = self.querySelector('[data-tts-qty-input]');
      if (!input) return;
      var current = parseInt(input.value, 10) || 1;
      var next = btn.getAttribute('data-tts-qty-change') === 'up' ? current + 1 : current - 1;
      input.value = Math.max(1, next);
    });

    this.refreshOptionAvailability();
    this.refreshUI();
    this.updateDesignPreview();
  };

  /**
   * Deep-link support: ?Design=Drama+Bear&Size=M pre-checks the matching
   * radio inputs on load (used by the garment switcher and by outside
   * links/ads that want to land straight on a specific bear).
   */
  ProductForm.prototype.applyUrlPreselection = function () {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var self = this;
    var matchedAny = false;
    (this.product.options || []).forEach(function (name, index) {
      var raw = null;
      params.forEach(function (value, key) {
        if (key.toLowerCase() === (name || '').toLowerCase()) raw = value;
      });
      if (raw === null) return;
      var input = self.querySelector('input[data-option-position="' + (index + 1) + '"][value="' + CSS.escape(raw) + '"]');
      if (input) {
        input.checked = true;
        matchedAny = true;
      }
    });
    if (matchedAny) {
      var options = this.selectedOptions();
      var match = this.findVariant(options);
      if (match) {
        this.currentVariant = match;
        this.idInput.value = match.id;
      }
    }
  };

  ProductForm.prototype.getSelectedDesignName = function () {
    if (this.designOptionIndex < 0) return null;
    return this.selectedOptions()[this.designOptionIndex] || null;
  };

  ProductForm.prototype.findDesign = function (name) {
    if (!name) return null;
    return (this.designs || []).find(function (d) { return d.name === name; }) || null;
  };

  /**
   * Keeps the print-preview overlay, bear bio, mature badge, hidden
   * "Bear" line property and garment-switch links in sync with
   * whichever "Design" swatch is currently selected.
   */
  ProductForm.prototype.updateDesignPreview = function () {
    var name = this.getSelectedDesignName();
    var design = this.findDesign(name);

    var overlay = document.querySelector('[data-tts-print-overlay]');
    if (overlay) {
      if (design && design.artwork) {
        overlay.src = design.artwork;
        overlay.alt = name;
        overlay.hidden = false;
      } else {
        overlay.hidden = true;
        overlay.removeAttribute('src');
      }
    }

    var bearProp = this.querySelector('[data-tts-bear-property]');
    if (bearProp && name) bearProp.value = name;

    var matureBadge = document.querySelector('[data-tts-mature-badge]');
    if (matureBadge) matureBadge.hidden = !(design && design.maturity_tier === 'mature');

    var hookEl = this.querySelector('[data-tts-hook]');
    if (hookEl && design && design.short_personality) hookEl.textContent = design.short_personality;

    if (design) {
      var bioNameEl = document.querySelector('[data-tts-bio-name]');
      var bioTaglineEl = document.querySelector('[data-tts-bio-tagline]');
      var bioTextEl = document.querySelector('[data-tts-bio-text]');
      var bioArtEl = document.querySelector('[data-tts-bio-art]');
      if (bioNameEl) bioNameEl.textContent = design.name;
      if (bioTaglineEl) {
        if (design.tagline) {
          bioTaglineEl.textContent = '“' + design.tagline + '”';
          bioTaglineEl.hidden = false;
        } else {
          bioTaglineEl.hidden = true;
        }
      }
      if (bioTextEl) bioTextEl.textContent = design.description || design.short_personality || '';
      if (bioArtEl && (design.artwork || design.thumbnail)) {
        var artSrc = design.artwork || design.thumbnail;
        var img = bioArtEl.querySelector('img');
        if (img) {
          img.src = artSrc;
          img.alt = design.name;
        } else {
          bioArtEl.innerHTML = '<img src="' + artSrc + '" alt="' + design.name.replace(/"/g, '&quot;') + '" loading="lazy">';
        }
      }
    }

    var currentSize = this.designOptionIndex >= 0 ? this.selectedOptions()[this.designOptionIndex === 0 ? 1 : 0] : null;
    document.querySelectorAll('[data-tts-garment-link]').forEach(function (link) {
      var base = link.getAttribute('data-base-url');
      if (!base || !name) return;
      var url = new URL(base, window.location.origin);
      url.searchParams.set('Design', name);
      if (currentSize) url.searchParams.set('Size', currentSize);
      link.setAttribute('href', url.pathname + url.search);
    });
  };

  ProductForm.prototype.selectedOptions = function () {
    var options = [];
    var inputs = this.querySelectorAll('[data-tts-option-input]:checked, select[data-tts-option-input]');
    inputs.forEach(function (input) {
      options[parseInt(input.getAttribute('data-option-position'), 10) - 1] = input.value;
    });
    return options;
  };

  ProductForm.prototype.findVariantById = function (id) {
    return (this.product.variants || []).find(function (v) { return v.id === id; });
  };

  ProductForm.prototype.findVariant = function (options) {
    return (this.product.variants || []).find(function (variant) {
      return variant.options.every(function (value, index) {
        return options[index] === undefined || value === options[index];
      });
    });
  };

  ProductForm.prototype.onOptionChange = function () {
    var options = this.selectedOptions();
    this.currentVariant = this.findVariant(options) || null;
    this.refreshOptionAvailability();
    this.refreshUI();
    this.updateDesignPreview();

    if (this.currentVariant) {
      // Keep the URL shareable / restorable
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.updateMediaForVariant(this.currentVariant);
      document.dispatchEvent(new CustomEvent('tts:variant:changed', {
        detail: { variant: this.currentVariant, product: this.product }
      }));
    }
  };

  /**
   * Cross off option values that cannot produce an available
   * variant given the other currently selected options.
   */
  ProductForm.prototype.refreshOptionAvailability = function () {
    var self = this;
    var selected = this.selectedOptions();
    this.querySelectorAll('[data-tts-option-input]').forEach(function (input) {
      var position = parseInt(input.getAttribute('data-option-position'), 10) - 1;
      var test = selected.slice();
      test[position] = input.value;
      var match = (self.product.variants || []).find(function (variant) {
        return variant.options.every(function (value, index) {
          return test[index] === undefined || value === test[index];
        });
      });
      var wrapper = input.closest('.tts-size, .tts-swatch, .tts-design-swatch');
      var unavailable = !match || !match.available;
      if (wrapper) wrapper.classList.toggle('is-unavailable', unavailable);
      input.setAttribute('data-unavailable', unavailable ? 'true' : 'false');
    });
  };

  ProductForm.prototype.refreshUI = function () {
    var variant = this.currentVariant;
    var priceEl = this.querySelector('[data-tts-price]');
    var compareEl = this.querySelector('[data-tts-compare-price]');
    var stockEl = this.querySelector('[data-tts-stock]');
    var atcBtn = this.querySelector('[data-tts-atc]');
    var atcText = this.querySelector('[data-tts-atc-text]');
    var priceWrap = this.querySelector('[data-tts-price-wrap]');

    // Selected-value labels e.g. "Colour: Black"
    var self = this;
    this.querySelectorAll('[data-tts-selected-value]').forEach(function (label) {
      var position = parseInt(label.getAttribute('data-tts-selected-value'), 10) - 1;
      var options = self.selectedOptions();
      label.textContent = options[position] || '';
    });

    if (!variant) {
      if (atcBtn) {
        atcBtn.setAttribute('disabled', 'disabled');
        if (atcText) atcText.textContent = atcBtn.getAttribute('data-text-unavailable') || 'Unavailable';
      }
      if (stockEl) { stockEl.textContent = ''; }
      return;
    }

    this.idInput.value = variant.id;

    if (priceEl) priceEl.textContent = window.TTSFormatMoney(variant.price, this.moneyFormat);
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = window.TTSFormatMoney(variant.compare_at_price, this.moneyFormat);
        compareEl.hidden = false;
        if (priceWrap) priceWrap.classList.add('tts-price--sale');
      } else {
        compareEl.hidden = true;
        if (priceWrap) priceWrap.classList.remove('tts-price--sale');
      }
    }

    if (atcBtn) {
      if (variant.available) {
        atcBtn.removeAttribute('disabled');
        if (atcText) atcText.textContent = atcBtn.getAttribute('data-text-add') || 'Add to bag';
      } else {
        atcBtn.setAttribute('disabled', 'disabled');
        if (atcText) atcText.textContent = atcBtn.getAttribute('data-text-soldout') || 'Sold out';
      }
    }

    if (stockEl) {
      stockEl.classList.remove('tts-product__stock--ok', 'tts-product__stock--low', 'tts-product__stock--out');
      if (!variant.available) {
        stockEl.textContent = stockEl.getAttribute('data-text-out') || 'Sold out — pick another size and your other choices stay saved.';
        stockEl.classList.add('tts-product__stock--out');
      } else {
        stockEl.textContent = stockEl.getAttribute('data-text-in') || '';
        stockEl.classList.add('tts-product__stock--ok');
      }
    }

    // Sync sticky bar
    document.dispatchEvent(new CustomEvent('tts:product:ui', {
      detail: {
        price: window.TTSFormatMoney(variant.price, this.moneyFormat),
        title: variant.title,
        available: variant.available
      }
    }));
  };

  ProductForm.prototype.updateMediaForVariant = function (variant) {
    if (!variant.featured_media) return;
    var gallery = document.querySelector('tts-gallery');
    if (gallery && gallery.showMedia) gallery.showMedia(variant.featured_media.id);
  };

  ProductForm.prototype.showError = function (message) {
    var errorEl = this.querySelector('[data-tts-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.classList.add('is-visible');
    var self = this;
    window.clearTimeout(this._errorTimer);
    this._errorTimer = window.setTimeout(function () {
      errorEl.classList.remove('is-visible');
    }, 6000);
  };

  ProductForm.prototype.addToBag = function () {
    var self = this;
    var variant = this.currentVariant;

    if (!variant) {
      this.showError(this.getAttribute('data-text-choose') || 'Choose a size to add your bear to the bag.');
      return;
    }
    if (!variant.available) {
      this.showError(this.getAttribute('data-text-soldout-msg') || 'That option is sold out. Your other choices are still saved.');
      return;
    }

    var qtyInput = this.querySelector('[data-tts-qty-input]');
    var quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    var atcBtn = this.querySelector('[data-tts-atc]');

    var properties = {};
    this.querySelectorAll('[data-tts-line-property]').forEach(function (input) {
      if (input.value) properties[input.getAttribute('data-tts-line-property')] = input.value;
    });

    if (atcBtn) atcBtn.classList.add('is-loading');

    window.TTSCart.add([{ id: variant.id, quantity: quantity, properties: properties }])
      .then(function () {
        if (atcBtn) atcBtn.classList.remove('is-loading');
      })
      .catch(function (error) {
        if (atcBtn) atcBtn.classList.remove('is-loading');
        self.showError(error.message || 'We could not add that to your bag. Check your connection and try again.');
        document.dispatchEvent(new CustomEvent('tts:cart:error', { detail: { message: error.message } }));
      });
  };

  if (!window.customElements.get('tts-product-form')) {
    window.customElements.define('tts-product-form', ProductForm);
  }

  /* ----------------------------------------------------------
     <tts-gallery>
     ---------------------------------------------------------- */
  var Gallery = function () {
    var instance = Reflect.construct(HTMLElement, [], Gallery);
    return instance;
  };
  Gallery.prototype = Object.create(HTMLElement.prototype);
  Gallery.prototype.constructor = Gallery;
  Object.setPrototypeOf(Gallery, HTMLElement);

  Gallery.prototype.connectedCallback = function () {
    var self = this;
    this.mainImg = this.querySelector('[data-tts-gallery-main] img');
    this.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-tts-media-id]');
      if (!thumb) return;
      self.activate(thumb);
    });
  };

  Gallery.prototype.activate = function (thumb) {
    var src = thumb.getAttribute('data-full-src');
    var srcset = thumb.getAttribute('data-full-srcset');
    var alt = thumb.getAttribute('data-full-alt') || '';
    if (this.mainImg && src) {
      this.mainImg.src = src;
      if (srcset) this.mainImg.srcset = srcset;
      this.mainImg.alt = alt;
    }
    this.querySelectorAll('[data-tts-media-id]').forEach(function (t) {
      t.classList.toggle('is-active', t === thumb);
    });
  };

  Gallery.prototype.showMedia = function (mediaId) {
    var thumb = this.querySelector('[data-tts-media-id="' + mediaId + '"]');
    if (thumb) this.activate(thumb);
  };

  if (!window.customElements.get('tts-gallery')) {
    window.customElements.define('tts-gallery', Gallery);
  }

  /* ----------------------------------------------------------
     <tts-sticky-atc>
     Appears when the main buy area scrolls out of view.
     ---------------------------------------------------------- */
  var StickyAtc = function () {
    var instance = Reflect.construct(HTMLElement, [], StickyAtc);
    return instance;
  };
  StickyAtc.prototype = Object.create(HTMLElement.prototype);
  StickyAtc.prototype.constructor = StickyAtc;
  Object.setPrototypeOf(StickyAtc, HTMLElement);

  StickyAtc.prototype.connectedCallback = function () {
    var self = this;
    var targetSelector = this.getAttribute('data-watch') || '[data-tts-buy-area]';
    var target = document.querySelector(targetSelector);
    this.bar = this.querySelector('.tts-sticky-atc');
    if (!target || !this.bar || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    this.observer.observe(target);

    // Sticky button re-submits the main form
    this.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-tts-sticky-add]');
      if (!btn) return;
      var form = document.querySelector('tts-product-form');
      if (form && form.addToBag) {
        var mainBuy = document.querySelector('[data-tts-buy-area]');
        var variantChosen = form.currentVariant;
        if (!variantChosen && mainBuy) {
          mainBuy.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        form.addToBag();
      }
    });

    document.addEventListener('tts:product:ui', function (event) {
      var priceEl = self.querySelector('[data-tts-sticky-price]');
      var variantEl = self.querySelector('[data-tts-sticky-variant]');
      if (priceEl) priceEl.textContent = event.detail.price;
      if (variantEl) variantEl.textContent = event.detail.title;
    });
  };

  StickyAtc.prototype.disconnectedCallback = function () {
    if (this.observer) this.observer.disconnect();
  };

  if (!window.customElements.get('tts-sticky-atc')) {
    window.customElements.define('tts-sticky-atc', StickyAtc);
  }

  /* ----------------------------------------------------------
     <tts-modal-drawer> — generic drawer used for size guide
     and collection filters. Trigger: [data-tts-drawer-open]
     ---------------------------------------------------------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var ModalDrawer = function () {
    var instance = Reflect.construct(HTMLElement, [], ModalDrawer);
    return instance;
  };
  ModalDrawer.prototype = Object.create(HTMLElement.prototype);
  ModalDrawer.prototype.constructor = ModalDrawer;
  Object.setPrototypeOf(ModalDrawer, HTMLElement);

  ModalDrawer.prototype.connectedCallback = function () {
    var self = this;
    this.overlay = this.querySelector('[data-tts-overlay]');
    this.panel = this.querySelector('[data-tts-drawer]');
    this.name = this.getAttribute('data-drawer-name');
    this.lastFocused = null;
    this.isOpen = false;

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-tts-drawer-open="' + self.name + '"]');
      if (opener) {
        event.preventDefault();
        self.open();
      }
    });

    this.addEventListener('click', function (event) {
      if (event.target.closest('[data-tts-drawer-close]') || event.target === self.overlay) {
        self.close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') self.close();
      if (event.key === 'Tab') trap(self.panel, event);
    });

    function trap(container, event) {
      var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
  };

  ModalDrawer.prototype.open = function () {
    this.isOpen = true;
    this.lastFocused = document.activeElement;
    this.overlay.classList.add('is-open');
    this.panel.classList.add('is-open');
    this.panel.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    var closeBtn = this.panel.querySelector('[data-tts-drawer-close]');
    if (closeBtn) closeBtn.focus();
  };

  ModalDrawer.prototype.close = function () {
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
  };

  if (!window.customElements.get('tts-modal-drawer')) {
    window.customElements.define('tts-modal-drawer', ModalDrawer);
  }

  /* ----------------------------------------------------------
     <tts-carousel-nav> — arrow controls for scroll rails
     ---------------------------------------------------------- */
  var CarouselNav = function () {
    var instance = Reflect.construct(HTMLElement, [], CarouselNav);
    return instance;
  };
  CarouselNav.prototype = Object.create(HTMLElement.prototype);
  CarouselNav.prototype.constructor = CarouselNav;
  Object.setPrototypeOf(CarouselNav, HTMLElement);

  CarouselNav.prototype.connectedCallback = function () {
    var self = this;
    var railSelector = this.getAttribute('data-rail');
    this.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-dir]');
      if (!btn) return;
      var rail = document.querySelector(railSelector);
      if (!rail) return;
      var amount = rail.clientWidth * 0.8;
      rail.scrollBy({
        left: btn.getAttribute('data-dir') === 'next' ? amount : -amount,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });
  };

  if (!window.customElements.get('tts-carousel-nav')) {
    window.customElements.define('tts-carousel-nav', CarouselNav);
  }
})();
